import {
  FormControl,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material';
import { FC } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import { Controller } from 'react-hook-form';
import { TKeyValue } from '../../../utils/commonInterfaces';
import FieldGridContainer from '../fieldGridContainer';
import { useTranslation } from 'src/hooks/useTranslationHook';
// import FieldGridContainer from "../fieldGridContainer";

const SelectFieldErrorFallback: FC<{ name: string }> = ({ name }) => {
  const { translateText } = useTranslation();
  return (
    <FormControl fullWidth error>
      <Typography variant="caption" color="error">
        {translateText('common.fieldError', { fieldName: name })}
      </Typography>
    </FormControl>
  );
};

export type ControlledSelectFieldProps = {
  label: string;
  id: string;
  name: string;
  value?: string;
  defaultValue?: string;
  onChangeHandler?: (event: SelectChangeEvent) => void;
  itemList: TKeyValue<string | number>[];
  isRequired?: boolean;
  helperText?: string;
  control?: any;
  isGridItem?: boolean;
  errorMessage?: string;
  onChange?: (event: React.ChangeEvent<{ value: unknown }>) => void;
  dataTestId?: string;
  isFullWidth?: boolean;
  placeholder?: string;
  size?: 'small' | 'medium';
};

const ControlledSelectField: FC<ControlledSelectFieldProps> = (
  props: ControlledSelectFieldProps
) => {
  const { translateText } = useTranslation();
  const {
    label,
    id,
    name,
    value = '',
    defaultValue = '',
    itemList = [],
    isRequired = false,
    helperText = '',
    control,
    errorMessage,
    isGridItem = true,
    dataTestId = 'select-field-container',
    isFullWidth = true,
    placeholder = translateText('common.select'),
    size = 'small',
    onChangeHandler,
  } = props;

  const firstValue = { key: 'select', value: translateText('common.select') };
  // Ensure itemList is always an array and filter out invalid items
  const validItemList = Array.isArray(itemList)
    ? itemList.filter(
      (item: any) => item && item.key != null && item.value != null
    )
    : [firstValue];

  if (validItemList[0]?.key !== 'select') {
    validItemList.unshift(firstValue);
  }

  // Helper function to validate and normalize the value
  const getValidValue = (value: any): string | number => {
    if (value === null || value === undefined || value === '') {
      return '';
    }

    // Check for NaN
    if (typeof value === 'number' && isNaN(value)) {
      return '';
    }

    // Find the matching item in validItemList (handle type coercion)
    const matchingItem = validItemList.find((item) => {
      // Direct match
      if (item.key === value) return true;

      // String comparison
      if (String(item.key) === String(value)) return true;

      // Number comparison (only if both are valid numbers)
      const itemKeyNum = Number(item.key);
      const valueNum = Number(value);
      if (!isNaN(itemKeyNum) && !isNaN(valueNum) && itemKeyNum === valueNum) {
        return true;
      }

      return false;
    });

    if (!matchingItem) {
      return '';
    }

    // Ensure the key is not NaN before returning
    const key = matchingItem.key;
    if (typeof key === 'number' && isNaN(key)) {
      return '';
    }

    // Return the actual key from the itemList to ensure type consistency
    return key;
  };

  const renderSelectField = () => {
    return (
      <ErrorBoundary
        FallbackComponent={() => {
          return <SelectFieldErrorFallback name={name} />;
        }}
        onError={(error, errorInfo) => {
          console.error(
            'ControlledSelectField error:',
            error,
            errorInfo,
            name,
            id
          );
        }}
      >
        {control ? (
          <Controller
            name={name}
            control={control}
            render={({ field }) => {
              const safeValue = getValidValue(field.value);

              return (
                <FormControl
                  fullWidth
                  required={isRequired}
                  error={!!errorMessage}
                  size={size}
                  data-testid={dataTestId}
                >
                  <InputLabel
                    id={id}
                    sx={{
                      fontSize: {
                        xs: '0.875rem',
                        sm: '1rem',
                      },
                    }}
                  >
                    {label}
                  </InputLabel>
                  <Select
                    name={field.name}
                    label={label}
                    id={id + 'field'}
                    size={size}
                    value={safeValue}
                    onChange={(e) => {
                      const selectedValue = e.target.value;
                      // Treat 'select' placeholder and empty string as null
                      const newValue =
                        selectedValue === '' || selectedValue === 'select'
                          ? null
                          : selectedValue;
                      field.onChange(newValue);
                    }}
                    onBlur={field.onBlur}
                    ref={field.ref}
                    displayEmpty
                    sx={{
                      '& .MuiSelect-select': {
                        minHeight: '24px',
                        fontSize: {
                          xs: '16px', // Prevents zoom on iOS
                          sm: '14px',
                        },
                        display: 'flex',
                        alignItems: 'center',
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          '& .MuiMenuItem-root': {
                            minHeight: '44px',
                            fontSize: {
                              xs: '16px',
                              sm: '14px',
                            },
                            padding: {
                              xs: '12px 16px',
                              sm: '8px 16px',
                            },
                          },
                        },
                      },
                    }}
                  >
                    {validItemList.map((item: any) => {
                      // Ensure key is not NaN
                      const itemKey = item.key;
                      const safeKey =
                        itemKey === null ||
                          itemKey === undefined ||
                          (typeof itemKey === 'number' && isNaN(itemKey))
                          ? ''
                          : itemKey;

                      return (
                        <MenuItem key={safeKey} value={safeKey}>
                          <ListItemText sx={{ textAlign: 'left' }}>
                            {item.value}
                          </ListItemText>
                        </MenuItem>
                      );
                    })}
                  </Select>
                  {(errorMessage || helperText) && (
                    <FormHelperText error={!!errorMessage}>
                      {errorMessage || helperText}
                    </FormHelperText>
                  )}
                </FormControl>
              );
            }}
          />
        ) : (
          <FormControl
            fullWidth
            required={isRequired}
            size={size}
            data-testid={dataTestId}
          >
            <InputLabel
              id={id}
              sx={{
                fontSize: {
                  xs: '0.875rem',
                  sm: '1rem',
                },
              }}
            >
              {label}
            </InputLabel>
            <Select
              size={size}
              labelId={id + 'label'}
              id={id + 'field'}
              label={label}
              name={name}
              defaultValue={defaultValue}
              value={value}
              placeholder={placeholder ?? translateText('common.select')}
              onChange={onChangeHandler}
              sx={{
                '& .MuiSelect-select': {
                  minHeight: '32px',
                  fontSize: {
                    xs: '16px', // Prevents zoom on iOS
                    sm: '14px',
                  },
                  display: 'flex',
                  alignItems: 'center',
                },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    '& .MuiMenuItem-root': {
                      minHeight: '44px',
                      fontSize: {
                        xs: '16px',
                        sm: '14px',
                      },
                      padding: {
                        xs: '2px 8px',
                        sm: '2px 8px',
                      },
                    },
                  },
                },
              }}
            >
              {validItemList.map((item) => {
                return (
                  <MenuItem key={item.key} value={item.key}>
                    <ListItemText sx={{ textAlign: 'left' }}>
                      {item.value}
                    </ListItemText>
                  </MenuItem>
                );
              })}
              {helperText && <FormHelperText>{helperText}</FormHelperText>}
            </Select>
          </FormControl>
        )}
      </ErrorBoundary>
    );
  };

  if (isGridItem) {
    return (
      <ErrorBoundary
        FallbackComponent={() => {
          return <SelectFieldErrorFallback name={name} />;
        }}
        onError={(error, errorInfo) => {
          console.error('ControlledSelectField error:', name, id);
        }}
      >
        <FieldGridContainer isFullWidth={isFullWidth} dataTestId={dataTestId}>
          {renderSelectField()}
        </FieldGridContainer>
      </ErrorBoundary>
    );
  }

  return (
    <ErrorBoundary
      FallbackComponent={() => {
        return <SelectFieldErrorFallback name={name} />;
      }}
      onError={(error, errorInfo) => {
        console.error('ControlledSelectField error item:', name, id);
      }}
    >
      {renderSelectField()}
    </ErrorBoundary>
  );
};

export default ControlledSelectField;
